import React, { useState, useEffect } from 'react';
import { db } from './firebase'; 
import { collection, onSnapshot } from 'firebase/firestore'; 
import { FaGift, FaCheckCircle, FaCalculator, FaExclamationTriangle, FaClock } from 'react-icons/fa';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

function StatCard({ title, value, icon, colorClass, viewId, onNavigate }) {
    return (
        <div 
            onClick={() => onNavigate && onNavigate(viewId)}
            className={`bg-white p-6 rounded-2xl shadow-sm border-l-4 ${colorClass.border} flex items-center justify-between transition-all duration-300 hover:shadow-md hover:scale-105 cursor-pointer`}
        >
            <div>
                <p className="text-3xl font-black text-kokimiko-texto">{value}</p>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">{title}</p>
            </div>
            <div className={`p-3 rounded-xl ${colorClass.bg} ${colorClass.text} opacity-90`}>
                {icon}
            </div>
        </div>
    );
}

function DashboardView({ setActiveView }) {
    const [proyectos, setProyectos] = useState([]);
    const [itinerantes, setItinerantes] = useState([]); 
    const [stats, setStats] = useState({
        proyectosTransito: 0,
        completadosMes: 0,
        presupuestosMes: 0
    });
    const [cargando, setCargando] = useState(true);

    const formatearFecha = (fecha) => {
        if (!fecha) return 'SIN FECHA';
        if (fecha.seconds) return new Date(fecha.seconds * 1000).toLocaleDateString();
        return fecha.toString();
    };

    useEffect(() => {
        const unsubProyectos = onSnapshot(collection(db, 'proyectosEnTransito'), (snapshot) => {
            const docs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setProyectos(docs);
            setStats(prev => ({
                ...prev,
                proyectosTransito: docs.filter(p => p.estado !== 'Completado').length,
                completadosMes: docs.filter(p => p.estado === 'Completado').length
            }));
            setCargando(false);
        });

        const unsubPresupuestos = onSnapshot(collection(db, 'presupuestos'), (snapshot) => {
            setStats(prev => ({ ...prev, presupuestosMes: snapshot.size }));
        });

        const unsubItinerantes = onSnapshot(collection(db, 'reservasHoras'), (snapshot) => {
            const docs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setItinerantes(docs);
        });

        return () => {
            unsubProyectos();
            unsubPresupuestos();
            unsubItinerantes();
        };
    }, []);

    const dataGrafico = [
        { name: 'Agendado', value: proyectos.filter(p => p.estado === 'Aprobado').length },
        { name: 'En Preparación', value: proyectos.filter(p => p.estado === 'En Producción').length },
        { name: 'Atención Req.', value: proyectos.filter(p => p.estado === 'Retrasado').length },
    ];
    
    // 🔥 COLORES ACTUALIZADOS AL NUEVO LOGO (Verde, Azul, Naranja) 🔥
    const COLORS = ['#5DBB63', '#71A5C4', '#E15A42'];

    if (cargando) return (
        <div className="h-full flex flex-col items-center justify-center space-y-4">
            <div className="w-12 h-12 border-4 border-kokimiko-azul border-t-transparent rounded-full animate-spin"></div>
            <div className="font-bold text-kokimiko-texto italic animate-pulse tracking-tighter">
                CARGANDO MAGIA DE KOKIMIKO...
            </div>
        </div>
    );

    return (
        <div className="p-6 bg-kokimiko-fondo/30 min-h-screen space-y-8 animate-fade-in">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-black text-kokimiko-texto uppercase tracking-wider">Resumen de Eventos</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard 
                    title="Eventos Activos" 
                    value={stats.proyectosTransito} 
                    icon={<FaGift size={24} />} 
                    colorClass={{ border: 'border-kokimiko-azul', bg: 'bg-kokimiko-azul/20', text: 'text-kokimiko-azul' }}
                    viewId="proyectos"
                    onNavigate={setActiveView}
                />
                <StatCard 
                    title="Fiestas Realizadas" 
                    value={stats.completadosMes} 
                    icon={<FaCheckCircle size={24} />} 
                    colorClass={{ border: 'border-kokimiko-verde', bg: 'bg-kokimiko-verde/20', text: 'text-kokimiko-verde' }}
                    viewId="proyectos"
                    onNavigate={setActiveView}
                />
                <StatCard 
                    title="Cotizaciones" 
                    value={stats.presupuestosMes} 
                    icon={<FaCalculator size={24} />} 
                    colorClass={{ border: 'border-kokimiko-teal', bg: 'bg-kokimiko-teal/20', text: 'text-kokimiko-teal' }}
                    viewId="presupuestos"
                    onNavigate={setActiveView}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100 h-96 transition-all duration-500 hover:shadow-lg">
                    <h3 className="font-black text-kokimiko-texto mb-6 uppercase text-xs tracking-widest text-center">Estatus de Fiestas y Talleres</h3>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie 
                                data={dataGrafico} 
                                innerRadius={70} 
                                outerRadius={100} 
                                paddingAngle={8} 
                                dataKey="value"
                                animationDuration={1500}
                            >
                                {dataGrafico.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend verticalAlign="bottom" />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                <div className="flex flex-col space-y-8">
                    
                    <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100 transition-all duration-500 hover:shadow-lg flex-1">
                        <h3 className="font-black text-kokimiko-texto mb-6 uppercase text-xs tracking-widest flex items-center gap-2">
                            <FaExclamationTriangle className="text-kokimiko-naranja animate-bounce" /> Próximos Eventos
                        </h3>
                        <div className="space-y-4 overflow-y-auto custom-scrollbar max-h-48">
                            {proyectos.filter(p => p.estado !== 'Completado').slice(0, 5).map((p, i) => (
                                <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100 transition-all hover:-translate-y-1 hover:shadow-md">
                                    <span className="text-sm font-bold text-kokimiko-texto">{p.nombreProyecto}</span>
                                    <span className="bg-white px-3 py-1 rounded-lg text-[10px] font-black text-kokimiko-azul border border-gray-200">
                                        {formatearFecha(p.fechaEntrega)}
                                    </span>
                                </div>
                            ))}
                            {proyectos.filter(p => p.estado !== 'Completado').length === 0 && (
                                <p className="text-center text-sm text-gray-400 italic">No hay eventos próximos agendados.</p>
                            )}
                        </div>
                    </div>

                    <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100 transition-all duration-500 hover:shadow-lg flex-1">
                        <h3 className="font-black text-kokimiko-texto mb-6 uppercase text-xs tracking-widest flex items-center gap-2">
                            <FaClock className="text-kokimiko-teal animate-pulse" /> Niños en Sala (Alquiler por Hora)
                        </h3>
                        <div className="space-y-4 overflow-y-auto custom-scrollbar max-h-48">
                            {itinerantes.length > 0 ? itinerantes.map((nino, i) => (
                                <div key={i} className="flex items-center justify-between p-4 bg-kokimiko-teal/10 rounded-2xl border border-kokimiko-teal/20 transition-all hover:-translate-y-1 hover:shadow-md">
                                    <span className="text-sm font-bold text-kokimiko-texto">{nino.nombreNino || 'Sin Nombre'}</span>
                                    <div className="flex gap-2">
                                        <span className="bg-white px-3 py-1 rounded-lg text-[10px] font-black text-kokimiko-verde border border-gray-200">
                                            {nino.horaEntrada || '00:00'}
                                        </span>
                                        <span className="bg-white px-3 py-1 rounded-lg text-[10px] font-black text-kokimiko-naranja border border-gray-200">
                                            {nino.horaSalida || '00:00'}
                                        </span>
                                    </div>
                                </div>
                            )) : (
                                <p className="text-center text-sm text-gray-400 italic">No hay niños itinerantes registrados en sala actualmente.</p>
                            )}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default DashboardView;